import { ReactNode } from "react";
import { TechAvailabilitySummary } from "../../modules/technicians/models/technician";

interface IProps {
    availability?: TechAvailabilitySummary
    className?: string
    // Shown when availability can't be determined (e.g. no resolvable timezone).
    fallback?: ReactNode
    // Where the row already has its own icon (the technician drawer), the dot is one too many.
    noDot?: boolean
}

// Shared technician availability label used by the job-form Select and the map
// tooltip so both read identically: "Available", "Available (08:00 – 18:00)",
// or "Off today", with a colored status dot.
/** The colour the label is in, for a row that colours its own icon to match (the drawer). */
export function techAvailabilityTone(a?: TechAvailabilitySummary): string {
    if (!a) return "gray-500";
    if (a.alwaysAvailable) return "success";
    if (a.hasHoursToday && a.todayFrom && a.todayTo) return a.openNow === false ? "warning" : "success";
    return "gray-500";
}

export default function TechAvailabilityLabel(props: IProps) {

    const a = props.availability;
    if (!a) return <>{props.fallback ?? null}</>;

    const range = a.todayFrom && a.todayTo ? `${a.todayFrom} – ${a.todayTo}` : undefined;

    let dot = "bg-gray-400", text = "text-gray-500", label = "Off today";
    let hoursRange: string | undefined;
    if (a.alwaysAvailable) { dot = "bg-success"; text = "text-success"; label = "Available"; }
    else if (a.hasHoursToday && range) {
        hoursRange = range;
        // Having a window today doesn't mean they're working right now: before it
        // starts (or after it ends) openNow is false, so show amber "Outside hours"
        // to match the chip on the job form instead of a misleading green "Available".
        if (a.openNow === false) { dot = "bg-warning"; text = "text-warning"; label = "Outside hours"; }
        else { dot = "bg-success"; text = "text-success"; label = "Available"; }
    }
    else if (a.openNow !== false) return <>{props.fallback ?? null}</>; // undetermined

    return (
        <span className={`d-inline-flex align-items-center ${text} ${props.className ?? ""}`}>
            {!props.noDot && <span className={`d-inline-block rounded-circle me-2 ${dot}`} style={{ width: 7, height: 7 }} />}
            {label}
            {hoursRange && <span className="fs-9 text-gray-500 ms-1">({hoursRange})</span>}
        </span>
    );
}
